import React, { createContext, useState, useContext } from 'react';
import { firestore, timestamp } from '../firebase/config';
import { collection, addDoc, updateDoc, doc, getDoc, getDocs, query, where, deleteDoc } from 'firebase/firestore';

// Création du contexte des entreprises
const CompanyContext = createContext();

// Hook personnalisé pour utiliser le contexte des entreprises
export const useCompany = () => {
  return useContext(CompanyContext);
};

// Fournisseur du contexte des entreprises
export const CompanyProvider = ({ children }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Fonction pour récupérer toutes les entreprises
  const getCompanies = async () => {
    try {
      setLoading(true);
      const companiesCollection = collection(firestore, 'companies');
      const companiesSnapshot = await getDocs(companiesCollection);
      const companiesList = companiesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCompanies(companiesList);
      setLoading(false);
      return companiesList;
    } catch (error) {
      setError(error.message);
      setLoading(false);
      throw error;
    }
  };

  // Fonction pour récupérer une entreprise par ID
  const getCompanyById = async (id) => {
    try {
      const companyDoc = await getDoc(doc(firestore, 'companies', id));
      if (companyDoc.exists()) {
        return { id: companyDoc.id, ...companyDoc.data() };
      } else {
        return null;
      }
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour créer une nouvelle entreprise
  const createCompany = async (companyData) => {
    try {
      const newCompany = {
        ...companyData,
        status: 'active',
        createdAt: timestamp(),
        updatedAt: timestamp()
      };
      const docRef = await addDoc(collection(firestore, 'companies'), newCompany);
      return { id: docRef.id, ...newCompany };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour mettre à jour une entreprise
  const updateCompany = async (id, companyData) => {
    try {
      const companyRef = doc(firestore, 'companies', id);
      const updatedData = {
        ...companyData,
        updatedAt: timestamp()
      };
      await updateDoc(companyRef, updatedData);
      return { id, ...updatedData };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Fonction pour désactiver une entreprise
  const deactivateCompany = async (id, reason) => {
    try {
      const companyRef = doc(firestore, 'companies', id);
      await updateDoc(companyRef, {
        status: 'inactive',
        deactivationReason: reason,
        deactivatedAt: timestamp(),
        updatedAt: timestamp()
      });
      return { id, status: 'inactive', deactivationReason: reason };
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Valeur du contexte
  const value = {
    companies,
    loading,
    error,
    getCompanies,
    getCompanyById,
    createCompany,
    updateCompany,
    deactivateCompany
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};

export { CompanyContext };
